import { Node, mergeAttributes } from '@tiptap/core'

export default Node.create({
  name: 'resizableImage',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      align: {
        default: 'left',
      },
      display: {
        default: 'block', // 'block', 'inline'
      },
      caption: {
        default: '',
      },
      captionPosition: {
        default: 'none', // 'top', 'bottom', 'none'
      },
      spacing: {
        default: 'medium', // 'small', 'medium', 'large'
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { width, height, align, display, caption, captionPosition, spacing, ...attrs } = HTMLAttributes
    
    // Determine spacing values
    const spacingMap = {
      small: '0.5em',
      medium: '1em',
      large: '1.5em'
    }
    const spacingValue = spacingMap[spacing] || spacingMap.medium
    
    // Build inline styles for image
    let imgStyle = 'max-width: 100%; height: auto; display: block;'
    
    if (width) {
      imgStyle += ` width: ${width}px;`
    }
    if (height) {
      imgStyle += ` height: ${height}px;`
    }
    
    // If no caption, return simple image with alignment
    if (!caption || captionPosition === 'none') {
      if (display === 'inline') {
        imgStyle = imgStyle.replace('display: block;', 'display: inline-block; vertical-align: middle;')
        
        if (align === 'center') {
          imgStyle += ' margin: 0 auto;'
        } else if (align === 'right') {
          imgStyle += ` float: right; margin-left: ${spacingValue};`
        } else if (align === 'left') {
          imgStyle += ` float: left; margin-right: ${spacingValue};`
        } else {
          imgStyle += ` margin: 0 ${spacingValue};`
        }
      } else {
        if (align === 'center') {
          imgStyle += ' margin-left: auto; margin-right: auto;'
        } else if (align === 'right') {
          imgStyle += ` float: right; margin-left: ${spacingValue}; margin-bottom: ${spacingValue};`
        } else if (align === 'left') {
          imgStyle += ` float: left; margin-right: ${spacingValue}; margin-bottom: ${spacingValue};`
        }
      }
      
      return ['img', mergeAttributes(attrs, { style: imgStyle })]
    }
    
    // With caption, wrap in figure
    let figureStyle = 'margin: 1em 0; max-width: 100%;'
    
    if (width) {
      figureStyle += ` width: ${width}px;`
    }
    
    if (display === 'inline') {
      figureStyle += ' display: inline-block; vertical-align: middle;'
      
      if (align === 'center') {
        figureStyle += ' margin: 0 auto;'
      } else if (align === 'right') {
        figureStyle += ` margin-left: auto; margin-right: 0;`
      } else if (align === 'left') {
        figureStyle += ` margin-left: 0; margin-right: auto;`
      } else {
        figureStyle += ` margin: 0 ${spacingValue};`
      }
    } else {
      figureStyle += ' display: block;'
      
      if (align === 'center') {
        figureStyle += ' margin-left: auto; margin-right: auto; text-align: center;'
      } else if (align === 'right') {
        figureStyle += ` float: right; margin-left: ${spacingValue}; margin-bottom: ${spacingValue};`
      } else if (align === 'left') {
        figureStyle += ` float: left; margin-right: ${spacingValue}; margin-bottom: ${spacingValue};`
      }
    }
    
    const captionStyle = 'font-size: 0.875em; color: #6b7280; padding: 0.5em; text-align: center; font-style: italic; margin: 0;'
    
    const img = ['img', mergeAttributes(attrs, { style: imgStyle })]
    const captionEl = ['figcaption', { style: captionStyle }, caption]
    
    const children = captionPosition === 'top' ? [captionEl, img] : [img, captionEl]
    
    return ['figure', { 
      style: figureStyle
    }, ...children]
  },

  addNodeView() {
    return ({ HTMLAttributes, getPos, editor }) => {
      const container = document.createElement('div')
      container.className = 'resizable-image-container'
      container.style.position = 'relative'
      container.style.maxWidth = '100%'
      container.style.margin = '1em 0'
      
      // Create inner wrapper for proper positioning
      const imageWrapper = document.createElement('div')
      imageWrapper.style.position = 'relative'
      imageWrapper.style.display = 'inline-block'
      imageWrapper.style.maxWidth = '100%'
      
      const display = HTMLAttributes.display || 'block'
      const align = HTMLAttributes.align || 'left'
      const spacing = HTMLAttributes.spacing || 'medium'
      
      // Determine spacing values
      const spacingMap = {
        small: '1em',
        medium: '1.5em',
        large: '2em'
      }
      const spacingValue = spacingMap[spacing] || spacingMap.medium
      
      if (display === 'inline') {
        container.style.display = 'inline-block'
        container.style.verticalAlign = 'middle'
        container.style.float = 'none'
        
        // Inline mode: no float, just positioning
        if (align === 'center') {
          container.style.margin = '0 auto'
        } else if (align === 'right') {
          container.style.marginLeft = 'auto'
          container.style.marginRight = '0'
        } else if (align === 'left') {
          container.style.marginLeft = '0'
          container.style.marginRight = 'auto'
        } else {
          container.style.margin = '0 0.5em'
        }
      } else {
        // Block mode: use float for text wrapping
        if (align === 'center') {
          container.style.display = 'block'
          container.style.textAlign = 'center'
          container.style.marginLeft = 'auto'
          container.style.marginRight = 'auto'
          container.style.float = 'none'
        } else {
          container.style.display = 'inline-block'
          
          if (align === 'right') {
            container.style.float = 'right'
            container.style.marginLeft = spacingValue
            container.style.marginBottom = spacingValue
          } else if (align === 'left') {
            container.style.float = 'left'
            container.style.marginRight = spacingValue
            container.style.marginBottom = spacingValue
          }
        }
      }

      // Caption elements
      const caption = HTMLAttributes.caption || ''
      const captionPosition = HTMLAttributes.captionPosition || 'none'
      
      let captionEl = null
      if (captionPosition !== 'none') {
        captionEl = document.createElement('div')
        captionEl.className = 'image-caption'
        captionEl.contentEditable = 'true'
        captionEl.textContent = caption || 'Enter caption...'
        captionEl.style.fontSize = '0.875em'
        captionEl.style.color = caption ? '#6b7280' : '#9ca3af'
        captionEl.style.padding = '0.5em'
        captionEl.style.textAlign = 'center'
        captionEl.style.fontStyle = 'italic'
        captionEl.style.outline = 'none'
        captionEl.style.cursor = 'text'
        captionEl.style.minHeight = '2em'
        captionEl.style.userSelect = 'text'
        captionEl.setAttribute('data-placeholder', 'Enter caption...')
        
        // Handle focus to clear placeholder
        captionEl.addEventListener('focus', (e) => {
          if (e.target.textContent === 'Enter caption...') {
            e.target.textContent = ''
            e.target.style.color = '#6b7280'
          }
        })
        
        // Handle blur to restore placeholder
        captionEl.addEventListener('blur', (e) => {
          const text = e.target.textContent.trim()
          if (text === '' || text === 'Enter caption...') {
            e.target.textContent = 'Enter caption...'
            e.target.style.color = '#9ca3af'
            if (typeof getPos === 'function') {
              const pos = getPos()
              if (pos !== undefined) {
                editor.commands.updateAttributes('resizableImage', { 
                  caption: '' 
                })
              }
            }
          } else {
            if (typeof getPos === 'function') {
              const pos = getPos()
              if (pos !== undefined) {
                editor.commands.updateAttributes('resizableImage', { 
                  caption: text 
                })
              }
            }
          }
        })
        
        captionEl.addEventListener('input', (e) => {
          const text = e.target.textContent
          if (text !== 'Enter caption...') {
            e.target.style.color = '#6b7280'
          }
        })
        
        captionEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            captionEl.blur()
          }
          // Allow text selection and editing
          e.stopPropagation()
        })
        
        captionEl.addEventListener('mousedown', (e) => {
          e.stopPropagation()
        })
        
        captionEl.addEventListener('click', (e) => {
          e.stopPropagation()
          // Focus the caption for editing
          captionEl.focus()
        })
        
        captionEl.addEventListener('dblclick', (e) => {
          e.stopPropagation()
        })
      }

      const img = document.createElement('img')
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value !== null && value !== undefined && !['caption', 'captionPosition', 'display'].includes(key)) {
          img.setAttribute(key, value)
        }
      })
      
      img.style.maxWidth = '100%'
      img.style.height = 'auto'
      img.style.display = 'block'
      img.style.margin = align === 'center' ? '0 auto' : '0'

      // Add resize handles
      const resizeHandle = document.createElement('div')
      resizeHandle.className = 'resize-handle'
      resizeHandle.style.position = 'absolute'
      resizeHandle.style.bottom = captionEl && captionPosition === 'bottom' ? '30px' : '0'
      resizeHandle.style.right = '0'
      resizeHandle.style.width = '12px'
      resizeHandle.style.height = '12px'
      resizeHandle.style.background = '#3b82f6'
      resizeHandle.style.cursor = 'se-resize'
      resizeHandle.style.opacity = '0'
      resizeHandle.style.transition = 'opacity 0.2s'
      resizeHandle.style.borderRadius = '3px'
      resizeHandle.style.border = '2px solid white'
      resizeHandle.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'
      resizeHandle.style.zIndex = '10'

      // Assemble container
      if (captionEl && captionPosition === 'top') {
        imageWrapper.appendChild(captionEl)
      }
      imageWrapper.appendChild(img)
      if (captionEl && captionPosition === 'bottom') {
        imageWrapper.appendChild(captionEl)
      }
      imageWrapper.appendChild(resizeHandle)
      
      container.appendChild(imageWrapper)

      // Show/hide controls on hover
      imageWrapper.addEventListener('mouseenter', () => {
        resizeHandle.style.opacity = '1'
      })

      imageWrapper.addEventListener('mouseleave', () => {
        resizeHandle.style.opacity = '0'
      })

      // Resize functionality
      let isResizing = false
      let startX, startY, startWidth, startHeight, aspectRatio

      resizeHandle.addEventListener('mousedown', (e) => {
        isResizing = true
        startX = e.clientX
        startY = e.clientY
        startWidth = parseInt(document.defaultView.getComputedStyle(img).width, 10)
        startHeight = parseInt(document.defaultView.getComputedStyle(img).height, 10)
        aspectRatio = startHeight / startWidth
        
        imageWrapper.style.outline = '2px solid #3b82f6'
        imageWrapper.style.outlineOffset = '2px'
        
        e.preventDefault()
      })

      document.addEventListener('mousemove', (e) => {
        if (!isResizing) return

        const deltaX = e.clientX - startX
        const deltaY = e.clientY - startY
        
        const delta = Math.max(deltaX, deltaY)
        const newWidth = Math.max(50, Math.min(800, startWidth + delta))
        const newHeight = Math.round(newWidth * aspectRatio)

        requestAnimationFrame(() => {
          img.style.width = newWidth + 'px'
          img.style.height = newHeight + 'px'
          
          imageWrapper.style.width = newWidth + 'px'
        })
      })

      document.addEventListener('mouseup', () => {
        if (isResizing) {
          isResizing = false
          
          imageWrapper.style.outline = ''
          imageWrapper.style.outlineOffset = ''
          
          const newWidth = parseInt(img.style.width, 10)
          const newHeight = parseInt(img.style.height, 10)
          
          if (typeof getPos === 'function') {
            editor.commands.updateAttributes('resizableImage', {
              width: newWidth,
              height: newHeight,
            })
          }
        }
      })

      // Make image selectable by clicking
      container.addEventListener('click', (e) => {
        // Don't select if clicking on caption (allow editing)
        if (captionEl && (e.target === captionEl || captionEl.contains(e.target))) {
          return
        }
        
        if (e.target === img || e.target === imageWrapper || e.target === container) {
          if (typeof getPos === 'function') {
            const pos = getPos()
            editor.commands.setNodeSelection(pos)
          }
        }
      })

      return {
        dom: container,
        contentDOM: captionEl,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'resizableImage') {
            return false
          }

          const newAttrs = updatedNode.attrs
          const oldDisplay = display
          const oldAlign = align
          const oldCaptionPosition = captionPosition
          const oldSpacing = spacing

          // Only force re-render if structural attributes changed
          if (newAttrs.display !== oldDisplay || 
              newAttrs.align !== oldAlign || 
              newAttrs.captionPosition !== oldCaptionPosition ||
              newAttrs.spacing !== oldSpacing) {
            return false
          }

          // Update image attributes without re-rendering
          Object.entries(newAttrs).forEach(([key, value]) => {
            if (value !== null && value !== undefined && !['caption', 'captionPosition', 'display'].includes(key)) {
              img.setAttribute(key, value)
            }
          })

          // Update caption text if it exists
          if (captionEl && newAttrs.caption !== undefined) {
            if (document.activeElement !== captionEl) {
              captionEl.textContent = newAttrs.caption || 'Enter caption...'
              captionEl.style.color = newAttrs.caption ? '#6b7280' : '#9ca3af'
            }
          }

          return true
        },
      }
    }
  },

  addCommands() {
    return {
      setResizableImage: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: {
            display: 'block',
            align: 'left',
            caption: '',
            captionPosition: 'none',
            spacing: 'medium',
            ...options,
          },
        })
      },
    }
  },
})